import React, {useState, useRef, useMemo} from 'react'
import styled from 'styled-components'
import {SelectField, SuggestedOption} from './inputFieldDelegate';
import { MdRemoveCircleOutline } from "react-icons/md";
import { BsCalendar3 } from "react-icons/bs";
import { CalendarPicker, useCloseOnOutsidePress } from "../../content_management/reusableItems/calendar";
import { timeFormatter, dateFormatter, isValidZip, isValidState, cityNameFormatter, generateShortId } from '../../../supplementary_components/supplementaryMethods';
import { API_ROOT, sendRequest } from '../../../supplementary_components/requests';
import { OptionsContainer } from './inputFieldDelegate';
import { AddButton } from '../../content_management/reusableItems/buttons'
import { AddNewTaskButtonFrame } from './taskListField';

/** Shown in the field as MM/dd HH:MM AM (dateFormatter still yields MM/dd/YY internally). */
function checkInOutFieldDisplay(dateFromFormatter, timeFromFormatter) {
	const md = dateFromFormatter && dateFromFormatter.match(/^(\d{2})\/(\d{2})\/\d{2}$/);
	if (!md || !timeFromFormatter) {
		return dateFromFormatter && timeFromFormatter ? `${dateFromFormatter} ${timeFromFormatter}` : '';
	}
	return `${md[1]}/${md[2]} ${timeFromFormatter}`;
}

/** ISO wall time → string dateFormatter parses, then MM/dd + time for display. */
function getInitialCheckInOutFieldValue(iso) {
	if (!iso || typeof iso !== 'string') return '';
	const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?/);
	if (!m) return iso;
	const [, y, mo, d, h24, min] = m;
	const hour = parseInt(h24, 10);
	const minute = parseInt(min, 10);
	const ap = hour >= 12 ? 'PM' : 'AM';
	const h12 = hour % 12 || 12;
	const intermediate = `${y}-${mo}-${d} ${String(h12).padStart(2, '0')}:${String(minute).padStart(2, '0')} ${ap}`;
	const { date, time } = dateFormatter(intermediate);
	if (date && time) return checkInOutFieldDisplay(date, time);
	return `${mo}/${d} ${String(h12).padStart(2, '0')}:${String(minute).padStart(2, '0')} ${ap}`;
}

function yearFromApptDate(apptDate) {
	const m = String(apptDate || '').match(/^(\d{2})\/(\d{2})\/(\d{2})$/);
	if (!m) return new Date().getFullYear();
	const yy = parseInt(m[3], 10);
	const century = Math.floor(new Date().getFullYear() / 100) * 100;
	return century + yy;
}

function offsetForTimezone(timeZone) {
	try {
		if (!timeZone) return 'Z';
		const part = new Intl.DateTimeFormat('en-US', {
			timeZone,
			timeZoneName: 'longOffset',
		}).formatToParts(new Date()).find((p) => p.type === 'timeZoneName');
		if (!part) return 'Z';
		const m = part.value.match(/GMT([+-]\d{2}:\d{2})/);
		if (m) return m[1];
		if (part.value === 'GMT' || part.value === 'UTC') return 'Z';
	} catch (_) { /* ignore */ }
	return 'Z';
}

function displayDateTimeToIso(dateStr, timeStr, previousIso, seedYear, seedOffset = 'Z') {
	const dm = dateStr.match(/^(\d{2})\/(\d{2})\/(\d{2})$/);
	const tm = timeStr.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
	if (!dm || !tm) return null;
	const [, mo, day, yy] = dm;
	let h = parseInt(tm[1], 10);
	const mi = parseInt(tm[2], 10);
	const ap = tm[3].toUpperCase();
	if (ap === 'PM' && h !== 12) h += 12;
	if (ap === 'AM' && h === 12) h = 0;

	let centuryBase = seedYear || new Date().getFullYear();
	let seconds = '';
	let offset = seedOffset || 'Z';
	if (previousIso && typeof previousIso === 'string') {
		const prevY = previousIso.match(/^(\d{4})-/);
		if (prevY) centuryBase = parseInt(prevY[1], 10);
		const timeSeg = previousIso.match(/T\d{2}:\d{2}(?::(\d{2}))?/);
		seconds = timeSeg && timeSeg[1] ? `:${timeSeg[1]}` : '';
		const offMatch = previousIso.match(/(Z|[+-]\d{2}:\d{2})$/);
		if (offMatch) offset = offMatch[1];
	}
	const year = Math.floor(centuryBase / 100) * 100 + parseInt(yy, 10);
	return `${year}-${mo}-${day}T${String(h).padStart(2, '0')}:${String(mi).padStart(2, '0')}${seconds}${offset === 'Z' ? 'Z' : offset}`;
}

function clearCheckTimeField(e, setTime) {
	e.target.removeAttribute('data-raw');
	e.target.value = '';
	if (typeof setTime === 'function') setTime('');
}

function handleActualCheckTimeBlur(e, fallbackRaw, setTime, seedYear, seedOffset) {
	const val = String(e.target.value || '');
	const attrRaw = e.target.getAttribute('data-raw');
	const baseIso = (attrRaw || fallbackRaw) || '';
	const lastValidDisplay = baseIso ? getInitialCheckInOutFieldValue(baseIso) : '';

	if (!val.trim() || val.trim() === 'INVALID INPUT') {
		clearCheckTimeField(e, setTime);
		return;
	}

	const { date, time } = dateFormatter(val);
	if (date && time) {
		const iso = displayDateTimeToIso(date, time, baseIso || null, seedYear, seedOffset);
		if (iso) {
			e.target.setAttribute('data-raw', iso);
			if (typeof setTime === 'function') setTime(iso);
			e.target.value = checkInOutFieldDisplay(date, time);
			return;
		}
	}

	e.target.style.color = 'var(--alert-color)';
	e.target.value = 'INVALID INPUT';
	setTimeout(() => {
		e.target.style.color = '';
		if (lastValidDisplay) {
			e.target.value = lastValidDisplay;
			if (baseIso) e.target.setAttribute('data-raw', baseIso);
		} else {
			clearCheckTimeField(e, setTime);
		}
	}, 500);
}

export const SubmenuContainer = styled.div`
	input, select, textarea{
		
		font-family:var(--main-font-family);
		font-size:1rem;
		font-weight:600;
		padding: 0.6rem;
		:not(:disabled){
			color:var(--dark-gray);
		}
		:disabled{
			color:var(--light-gray);
		}
		border:none;
		box-shadow: var(--button-outline);
		-moz-box-shadow: var(--button-outline);
		-webkit-box-shadow: var(--button-outline);
		transition:0.08s;
		border-radius:0.4rem;
		&:focus  {
			outline: rgba(48, 192, 140, 0.5) solid 0.25rem !important;
		}
		width:100%;
	}
	input, textarea{
		background: var(--bg);
    }
	
	input::placeholder, select::placeholder{
		color:#AAA;
		font-weight:300;

	}
	border:none;
	box-shadow: var(--button-outline);
	-moz-box-shadow: var(--button-outline);
	-webkit-box-shadow: var(--button-outline);
	margin-top:${props => props.$topMargin};
	padding-bottom:${props => props.$bottomPadding};
	border-radius:0.4rem;
	&>div:not(.removePadding){
		padding: 0 0.5rem;
		padding-top:0.5rem;
	}
	&>div:not(.removePadding, .noPadding){
		padding: 0.5rem;
	}
	&>div.addButton{
		padding:0.7rem;
	}

	&>div.firstStop{
		padding:0.7rem 0.7rem 0 0.7rem;
	}
	&>div.notFirstStop{
		padding:0 0.7rem;
	}
	&>div.mainAddButton{
		margin-top: 1.4rem
	}
	
	&>div{
		&>span{
			gap:0.5rem;
			:not(.hidden){
				display: grid;
			}
			&.hidden{
				display:none;
			}
			${props => props.$gridTemplateColumns ? `grid-template-columns:${props.$gridTemplateColumns}` : ''};
			${props => props.$gridTemplateAreas ? `grid-template-areas:${props.$gridTemplateAreas}` : ''};
		}
		&>div>span{
			gap:0.5rem;
			display: grid;
		}
		.template1{
			grid-template-columns: 1fr 35% ;
			margin-bottom:0.6rem;
		}
		.template3{
			grid-template-columns: 1fr 15% 35%;
			margin-bottom:0.6rem;
		}
		.templateHalfAndHald{
			grid-template-columns: 1fr 1fr;
			margin-bottom:0.5rem;
		}
		.noTemplate{
			grid-template-columns: 1fr;
			margin-bottom:0.5rem;
		}
		
	}
	.hiddenField{
		display:none;
	}
	.taskDragHandle{
		color:var(--dark-gray);
	}
`
const StopLabel = styled.div`
	display:flex;
	justify-content:space-between;
	align-items:center;
	user-select: none;
	-moz-user-select: none;
	-khtml-user-select: none;
	-webkit-user-select: none;
	-o-user-select: none;
	margin-bottom: 0.5rem;
	&>span:first-child{
		width:fit-content;
		padding: 0.2rem 0.8rem ;
		border-radius:0.4rem;
		
		color:var(--main-color);
		font-size:0.8rem;
		font-weight:bold;
		background-color: var(--completed-background-transparent);
	}
	&>span:nth-child(2){
		display:flex;
		align-items:center;
		margin-right:0.5rem;
		cursor:pointer;
		color:var(--alert-color);
		opacity:0.5;
		:hover{
			opacity:1;
			
		}
	}
`
const StopStatusLabel = styled.div`
	width:100%;
	height:100%;
	display:flex;
	justify-content:end;
	align-items:center;
	font-size:1rem;
	font-weight:600;
	color:var(--dark-gray);
`;

/** Inline calendar for load stop text fields: input stays first in DOM; icon absolutely positioned over the right. */
const LoadStopCalendarRow = styled.span`
	display: block;
	width: 100%;
	position: relative;

	& > input {
		padding-right: 2.35rem;
	}
`;

const LoadStopCalendarBtn = styled.button`
	position: absolute;
	right: 0.45rem;
	top: 50%;
	transform: translateY(-50%);
	z-index: 1;
	display: flex;
	align-items: center;
	justify-content: center;
	width: 1.75rem;
	height: 1.75rem;
	padding: 0;
	margin: 0;
	border: none;
	border-radius: 0.3rem;
	background: transparent;
	color: var(--main-color);
	cursor: pointer;

	&:hover {
		background: var(--extra-light-gray);
	}
`;

const LOAD_STOP_CALENDAR_TYPES = new Set(['singleDate', 'range', 'both']);

function normalizeStopCalendarPickType(t) {
	const s = t && String(t).toLowerCase();
	if (s && LOAD_STOP_CALENDAR_TYPES.has(s)) return s;
	return 'singleDate';
}

function stripStopCalendarDay(d) {
	return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function formatStopCalendarPickForInput(d) {
	if (!d) return '';
	const t = stripStopCalendarDay(d);
	const y = t.getFullYear();
	const mo = t.getMonth() + 1;
	const day = t.getDate();
	const intermediate = `${y}-${String(mo).padStart(2, '0')}-${String(day).padStart(2, '0')} 12:00 AM`;
	const { date } = dateFormatter(intermediate);
	if (date) return date;
	return `${String(mo).padStart(2, '0')}/${String(day).padStart(2, '0')}/${String(y).slice(-2)}`;
}

function parseStopFieldCalendarFragment(fragment) {
	if (!fragment || !String(fragment).trim()) return null;
	const { date, time } = dateFormatter(fragment.trim());
	if (!date) return null;
	const dm = date.match(/^(\d{2})\/(\d{2})\/(\d{2})$/);
	if (!dm) return null;
	const [, mo, d, yy] = dm;
	const year = 2000 + parseInt(yy, 10);
	if (!time) {
		return new Date(year, parseInt(mo, 10) - 1, parseInt(d, 10), 0, 0);
	}
	const tm = time.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
	if (!tm) return null;
	let h = parseInt(tm[1], 10);
	const mi = parseInt(tm[2], 10);
	const ap = tm[3].toUpperCase();
	if (ap === 'PM' && h !== 12) h += 12;
	if (ap === 'AM' && h === 12) h = 0;
	return new Date(year, parseInt(mo, 10) - 1, parseInt(d, 10), h, mi);
}

function parseStopFieldToCalendarInitialDates(raw, selectType) {
	if (!raw || !String(raw).trim()) return { initialStart: null, initialEnd: null };
	const rangeParts = String(raw).trim().split(/\s*(?:-|–|—)\s*/).filter((p) => p.length > 0);
	const canParseTwo =
		selectType !== 'singleDate' &&
		rangeParts.length >= 2 &&
		parseStopFieldCalendarFragment(rangeParts[0]) &&
		parseStopFieldCalendarFragment(rangeParts[1]);
	if (canParseTwo) {
		const a = parseStopFieldCalendarFragment(rangeParts[0]);
		const b = parseStopFieldCalendarFragment(rangeParts[1]);
		return {
			initialStart: a ? stripStopCalendarDay(a) : null,
			initialEnd: b ? stripStopCalendarDay(b) : null,
		};
	}
	const one = parseStopFieldCalendarFragment(String(raw).trim());
	return { initialStart: one ? stripStopCalendarDay(one) : null, initialEnd: null };
}
export class Stop{
	identifier ;
	stopOrderNumber ;
	stopTypeStatic = '';
	suggestionsList = [];
	companyName = '';
	addressLine1  = '';
	addressLine2  = '';
	city  = '';
	state  = '';
	postalCode  = '';
	stopType  = '';
	
	apptDate = '';
	apptTime = '';
	loadingType = '';
	refNumber = '';
	status = '';
	checkIn = '';
	checkOut = '';
	latLng  = '';
	timezone  = '';
	distanceTo = '';
	possiblyLate = '';
	zonedDateTime = '';
	constructor (input){
		// this.identifier = ;
		if (typeof input === 'string') {
			this.stopType = input;
		}else if (typeof input === 'object' && input !== null) {
			for (let key in this){
				this[key] = input[key];
			}
		}
		
	}
}
export const LoadStopsInput = ({column, editMode, newTaskAddressMode, setTaskAddress, noDateTime, initiallyInvisible}) => {
	let newStopPU = new Stop('PU')
	newStopPU.identifier = generateShortId();
	let newStopDEL = new Stop('DEL')
	newStopDEL.identifier = generateShortId();
	let defaultValueArray = newTaskAddressMode ? [newStopPU] : [newStopPU, newStopDEL];
	
	
	if(column.defaultValue && column.defaultValue()!==null){
		const DefaultValues = column.defaultValue(); 
		defaultValueArray = [];
		for(let i = 0; i < DefaultValues.length; i++){
			const customStop = typeof(DefaultValues[i]) !== 'string' ? DefaultValues[i] : JSON.parse(DefaultValues[i]);
			
			
			for (let key in customStop){
				const val = customStop[key];
				if(key !== 'address'){
					customStop[key] = val===null?'':val;
				}else{
					for (let subKey in val){
						const subVal = val[subKey];
						customStop[subKey] = subVal===null?'':subVal;
					}
				}
			}
			customStop["stopOrderNumber"] = null;
			customStop["stopTypeStatic"] = '';
			customStop["suggestionsList"] = [];
			const newStop = new Stop(customStop);
			newStop.identifier = generateShortId();
			defaultValueArray.push(newStop);
		}
	}  
	
	
	
	const [stopsArray, setStopsArray] = useState(defaultValueArray);
	// useEffect(() => {
	//     effect
	//     return () => {
	//         cleanup
	//     }
	// }, [input])
	const _setStopsArray = (val)=>{
		setTaskAddress(val)
		setStopsArray(val)
	}
	// _setStopsArray(defaultValueArray);
	// }, [setStopsArray])
	const removeStop = (e, index)=>{
		stopsArray.splice(index, 1)
		setStopsArray([...stopsArray]);
	};
	const addStop = ()=>{
		const newStop = new Stop('DEL');
		newStop.identifier = generateShortId();
		setStopsArray([...stopsArray, newStop])
	}
	const updateStopCheckTime = (index, field, iso) => {
		stopsArray[index][field] = iso;
		setStopsArray([...stopsArray]);
	};
	
	return newTaskAddressMode ? 
		<>
			{
				stopsArray.map((stop, index)=>{
					stop.stopOrderNumber = index;
					return singleStopView(stop, stopsArray.length, index, removeStop, stopsArray, _setStopsArray, editMode, newTaskAddressMode, noDateTime, initiallyInvisible)
				})
			}
		</>
		:
		<SubmenuContainer id='newLoadStops' $bottomPadding={'1rem'}>
			{
				stopsArray.map((stop, index)=>{
					stop.stopOrderNumber = index;
					return singleStopView(stop, stopsArray.length, index, removeStop, stopsArray, setStopsArray, editMode, null, null, null, updateStopCheckTime)
				})
			}
			<div className="mainAddButton removePadding">
				<AddButton action={addStop} />
			</div>
			
		</SubmenuContainer>
}
const singleStopView = (stop, arrayLength, index, removeStop, stopsArray, setStopsArray, editMode, newTaskAddressMode, noDateTime, initiallyInvisible, updateStopCheckTime ) =>{
	
	// const firstOrLastStop = stop.stopOrderNumber === 0 ? 'pickup' : (stop.stopOrderNumber === 1)?'delivery' : null;
	let removovingAllowed = arrayLength > 2;
	
	if(index === 0 || index === arrayLength-1){
		stop.stopTypeStatic = true;
		stop.stopType = index === 0 ? 'PU' : 'DEL';
	}else{
		stop.stopTypeStatic = false;
	}
	
	
	return <div className={`${index === 0 ? "firstStop" : "notFirstStop"} removePadding`} key={stop.identifier} id={newTaskAddressMode? 'newTaskAddingForm' : 'stop'+stop.stopOrderNumber}  style={newTaskAddressMode?{marginTop:"0.7rem", marginRight:"0.5rem", display : initiallyInvisible ? "none" : "block"}:{}}>
				{newTaskAddressMode ? 
					null
				:
					<>{index > 0 ?<AddNewTaskButtonFrame>
						<AddButton customSize={'1.2rem'} action={()=>{
							const newStop = new Stop('DEL');
							newStop.identifier = generateShortId();
							const updatedStops = [
							  ...stopsArray.slice(0, index),
							  newStop,
							  ...stopsArray.slice(index)
							];
							setStopsArray(updatedStops);
						}} /> </AddNewTaskButtonFrame>: null}
						<StopLabel style={index > 0 ? {marginTop:"0.4rem"}:{}}>
							<span id='stopOrderLabel'>{`Stop #${stop.stopOrderNumber+1}`}</span> 
							<span onClick={(e)=>{removeStop(e, stop.stopOrderNumber)}} style={{display:(!removovingAllowed)?"none":"block"}} title="Remove Stop">
							<MdRemoveCircleOutline /> 
							</span> 
						</StopLabel>
					</>
				}
				
				{
					newTaskAddressMode ? 
					FormStructureForNewTask.map((val)=>{
						if(noDateTime && val.partName === "dateTime")return null;
						return GenerateInputTag(val, stop, stopsArray, setStopsArray, editMode, index, true)
					})
					:
					FormStructure.map((val)=>{
						return GenerateInputTag(val, stop, stopsArray, setStopsArray, editMode, index, false)
					})
				}
				{(() => {
					const status = stop.status;
					const showCheckIn = status === 'inProgress' || status === 'completed' || !!stop.checkIn;
					const showCheckOut = status === 'completed' || !!stop.checkOut;
					const seedYear = yearFromApptDate(stop.apptDate);
					const seedOffset = offsetForTimezone(stop.timezone);
					return <>
						{showCheckIn ? <span className="template1">
							<StopStatusLabel>Check In</StopStatusLabel>
							<input
								name="checkIn"
								type="text"
								data-raw={stop.checkIn || ''}
								defaultValue={getInitialCheckInOutFieldValue(stop.checkIn)}
								onBlur={(e) => handleActualCheckTimeBlur(
									e,
									stop.checkIn,
									(iso) => updateStopCheckTime?.(index, 'checkIn', iso),
									seedYear,
									seedOffset
								)}
							/>
						</span> : null}
						{showCheckOut ? <span className="template1">
							<StopStatusLabel>Check Out</StopStatusLabel>
							<input
								name="checkOut"
								type="text"
								data-raw={stop.checkOut || ''}
								defaultValue={getInitialCheckInOutFieldValue(stop.checkOut)}
								onBlur={(e) => handleActualCheckTimeBlur(
									e,
									stop.checkOut,
									(iso) => updateStopCheckTime?.(index, 'checkOut', iso),
									seedYear,
									seedOffset
								)}
							/>
						</span> : null}
					</>;
				})()}
		</div>
	
}
const checkTrigger = (e, customOnChange, fieldName) => {
	setTimeout(() => {
		function invalidMessage(){
			e.target.style.color = 'var(--alert-color)'    
			e.target.value = 'INVALID INPUT'
			setTimeout(() => {
				e.target.value = ''
				e.target.style.color = '' 
				customOnChange('', fieldName, e, true)
			}, 500);
		}
		if(e.target.value === '') return;
		switch (e.target.id) {
			case 'postalCode':
				ZipCodeChecking(e, invalidMessage, customOnChange, fieldName);
				break;
			case 'apptDate':
				DateTimeChecking(e, invalidMessage, customOnChange);
				break;
			case 'apptTime':
				DateTimeChecking(e, invalidMessage, customOnChange);
				break;
			case 'state':
				StateValidation(e, invalidMessage, customOnChange, fieldName);
				break;
			default:
				break;
		}
	}, 10);
}

/** Address text fields that invalidate derived geo (latLng / timezone). */
const ADDRESS_GEO_TEXT_FIELDS = ['companyName', 'addressLine1', 'addressLine2', 'city', 'state', 'postalCode'];

function clearStopDerivedGeo(stopData) {
	stopData.latLng = '';
	stopData.timezone = '';
}

function sameAddressFieldValue(previous, next) {
	const normalize = (value) => (value == null ? '' : String(value).trim());
	return normalize(previous) === normalize(next);
}

/**
 * After applying a multi-field address update: clear derived geo unless the payload
 * already supplies latLng (suggestion select). Paste/parse never includes latLng.
 */
function applyAddressUpdateAndMaybeClearGeo(stopData, newVal, pasteTargetField) {
	let addressTextChanged = false;
	for (const fieldName of newVal['arrayOfFieldNames']) {
		if (pasteTargetField && fieldName !== pasteTargetField) {
			const existing = stopData[fieldName];
			if (existing != null && String(existing).trim() !== '') {
				continue;
			}
		}
		const next = newVal[fieldName] === null ? '' : newVal[fieldName];
		if (ADDRESS_GEO_TEXT_FIELDS.includes(fieldName) && !sameAddressFieldValue(stopData[fieldName], next)) {
			addressTextChanged = true;
		}
		stopData[fieldName] = next;
	}
	const providesLatLng = newVal['arrayOfFieldNames'].includes('latLng') && !sameAddressFieldValue('', newVal.latLng);
	if (addressTextChanged && !providesLatLng) {
		clearStopDerivedGeo(stopData);
	}
}

function GenerateInputTag(section, stop, stopsArray, setStopsArray, editMode, index, isNewTaskAddressForm = false) {
	if(section.onlyInEditMode && !editMode){
		return null;
	}
	function customOnChange (newVal, fieldName, e, onBlur){
		const data = [...stopsArray];
		const stopData = data[stop.stopOrderNumber];
		const addressTextChanged = ADDRESS_GEO_TEXT_FIELDS.includes(fieldName)
			&& !sameAddressFieldValue(stopData[fieldName], newVal);
		stopData[fieldName] = newVal;
		if (addressTextChanged) {
			clearStopDerivedGeo(stopData);
		}
		setStopsArray(data);
		if(onBlur && e && e.target){
			if(e.target.className.includes('subError') ){
				if(e.target.value !== ''){
					e.target.className = e.target.className.replace('subError', '')
				}
			}
		}
	}
	return <span key={section.templateName + section.fields[0].fieldName + index} className={section.templateName} >
		{section.fields.map((props, ndx)=>{
			const childKey = `${props.fieldName || props.labelText || 'field'}-${ndx}`;
			if (props.fieldType === 'label'){
				return <StopStatusLabel key={childKey}  >{props.labelText}</StopStatusLabel>
			}else if (props.fieldType === 'select'){
				return <SelectFieldGenerator key={childKey} props={props} stop={stop} customOnChange={customOnChange} />;
			}else{
				if (props.suggestionsEnabled){
					return <InputFieldWithSuggestionsGenerator key={childKey} props={props} customOnChange={customOnChange} stop={stop} stopsArray={stopsArray} setStopsArray={setStopsArray} />;
				}
				return <TextFieldGenerator
					key={childKey}
					props={props}
					stop={stop}
					customOnChange={customOnChange}
					index={index}
					stopsArray={stopsArray}
					setStopsArray={setStopsArray}
					calendarPopoverPlacement={isNewTaskAddressForm ? 'above' : 'below'}
					calendarPopoverAlign={isNewTaskAddressForm ? 'start' : 'end'}
				/>;
			}
		})}
	</span>
	
}
const TextFieldGenerator = ({
	props,
	stop,
	customOnChange,
	index,
	stopsArray,
	setStopsArray,
	calendarPopoverPlacement = 'below',
	calendarPopoverAlign = 'end',
}) => {
	const [calendarOpen, setCalendarOpen] = useState(false);
	const calendarShellRef = useRef(null);
	const calendarPopoverRef = useRef(null);
	const calendarOutsidePressRefs = useMemo(() => [calendarShellRef, calendarPopoverRef], []);
	useCloseOnOutsidePress(calendarOpen, calendarOutsidePressRefs, () => setCalendarOpen(false));

	function settAllParams(newVal, pasteTargetField) {
		if (!newVal || !newVal['arrayOfFieldNames']) return;
		const data = [...stopsArray];
		applyAddressUpdateAndMaybeClearGeo(data[stop.stopOrderNumber], newVal, pasteTargetField);
		for (const fieldName of newVal['arrayOfFieldNames']) {
			const stopParent = document.getElementById('stop' + stop.stopOrderNumber);
			if (stopParent !== null) {
				try {
					stopParent.querySelector(`#${fieldName}`).className = stopParent
						.querySelector(`#${fieldName}`)
						.className.replace('subError', '');
				} catch (error) {
				}
			}
		}
		setStopsArray(data);
	}

	if (!props.calendarPickType) {
		return (
			<input
				type="text"
				data-mandatory={props.required ? true : null}
				autoComplete={props.autoComplete}
				name={props.fieldName}
				placeholder={props.placeHolder}
				id={props.fieldName}
				onBlur={
					props.customCheckTrigger
						? (e) => {
								props.customCheckTrigger(e, customOnChange, props.fieldName);
							}
						: (e) => {
								if (e.target.value !== '') {
									customOnChange(
										props.customFormatter ? props.customFormatter(e.target.value) : e.target.value,
										props.fieldName,
										e,
										true
									);
								}
							}
				}
				onPaste={
					props.customCheckTrigger
						? (e) => {
								props.customCheckTrigger(e, customOnChange, props.fieldName, true);
							}
						: props.customPasteTrigger
							? async (e) => {
									try {
										const result = await props.customPasteTrigger(e);
										settAllParams(result, props.fieldName);
									} catch (error) {
									}
								}
							: null
				}
				onChange={(e) => {
					customOnChange(e.target.value, props.fieldName, e, false);
				}}
				value={stop[props.fieldName]}
				onFocus={(e) => {
					if (props.onFocus && e.target.value === '') {
						customOnChange(props.onFocus(index), props.fieldName, e, false);
					}
				}}
			/>
		);
	}

	const selectType = normalizeStopCalendarPickType(props.calendarPickType);
	const raw = stop[props.fieldName] || '';
	const { initialStart, initialEnd } = parseStopFieldToCalendarInitialDates(raw, selectType);

	const applyCalendarSelection = (...args) => {
		const a = args[0];
		const b = args[1];
		let val =
			b === undefined ? formatStopCalendarPickForInput(a) : `${formatStopCalendarPickForInput(a)} - ${formatStopCalendarPickForInput(b)}`;
		// if (props.customFormatter) {
		// 	val = props.customFormatter(val);
		// }
		const inp = calendarShellRef.current?.querySelector?.('input');
		customOnChange(val, props.fieldName, inp ? { target: inp } : null, true);
		// Calendar updates React state only — Form's change tracker listens for native DOM events.
		// Use `change` (not `input`) so React's text-input onChange does not re-fire with a stale value.
		if (inp) {
			inp.dispatchEvent(new Event('change', { bubbles: true }));
		}
		setCalendarOpen(false);
	};

	return (
		<LoadStopCalendarRow ref={calendarShellRef}>
			<input
				type="text"
				data-mandatory={props.required ? true : null}
				autoComplete={props.autoComplete}
				name={props.fieldName}
				placeholder={props.placeHolder}
				id={props.fieldName}
				onBlur={
					props.customCheckTrigger
						? (e) => {
								props.customCheckTrigger(e, customOnChange, props.fieldName);
							}
						: (e) => {
								if (e.target.value !== '') {
									customOnChange(
										props.customFormatter ? props.customFormatter(e.target.value) : e.target.value,
										props.fieldName,
										e,
										true
									);
								}
							}
				}
				onPaste={
					props.customCheckTrigger
						? (e) => {
								props.customCheckTrigger(e, customOnChange, props.fieldName, true);
							}
						: props.customPasteTrigger
							? async (e) => {
									try {
										const result = await props.customPasteTrigger(e);
										settAllParams(result, props.fieldName);
									} catch (error) {
									}
								}
							: null
				}
				onChange={(e) => {
					customOnChange(e.target.value, props.fieldName, e, false);
				}}
				value={stop[props.fieldName]}
				onFocus={(e) => {
					if (props.onFocus && e.target.value === '') {
						customOnChange(props.onFocus(index), props.fieldName, e, false);
					}
				}}
			/>
			<LoadStopCalendarBtn
				type="button"
				aria-label="Open calendar"
				tabIndex={-1}
				onMouseDown={(e) => e.preventDefault()}
				onClick={(e) => {
					e.stopPropagation();
					setCalendarOpen((o) => !o);
				}}
			>
				<BsCalendar3 />
			</LoadStopCalendarBtn>
			{calendarOpen ? (
				<CalendarPicker
					key={`${raw}-${selectType}-${calendarOpen}`}
					selectType={selectType}
					popoverAlign={calendarPopoverAlign}
					popoverPlacement={calendarPopoverPlacement}
					anchorRef={calendarShellRef}
					popoverRef={calendarPopoverRef}
					initialStart={initialStart || undefined}
					initialEnd={initialEnd || undefined}
					onCancel={() => setCalendarOpen(false)}
					onApply={applyCalendarSelection}
				/>
			) : null}
		</LoadStopCalendarRow>
	);
};
const SelectFieldGenerator = ({props, stop, customOnChange}) => {
	return <SelectField 
		disabled={props.fieldName === 'stopType' ? stop.stopTypeStatic : null}
		onChange={(e)=>{customOnChange(e.target.value, props.fieldName, e, false)}}   
		name={props.fieldName} 
		value={props.fieldName === 'stopType' ? stop.stopType : (props.fieldName === 'loadingType' ? stop.loadingType : props.fieldName === 'status' ? stop.status : null)}
		>
		{
			props.options.map((val, index)=>{
				return <option key={val.label + index} value={val.options} >{val.label}</option>
			})
		}
	</SelectField>
}

const InputFieldWithSuggestionsGenerator = ({props, customOnChange, stop, stopsArray, setStopsArray})=>{
	
	function obtainSuggestionsList(e){
		
		sendRequest('GET', props.suggestionsLink+e.target.value.trim(), null, (value)=>{
			// props.customCheckTrigger(value, customOnChange, props.fieldName, false)
			if(Array.isArray(value) && value.length > 0){
				e.target.nextSibling.style.display = 'block';  
			}else{
				e.target.nextSibling.style.display = 'none';  
			}
			customOnChange(value, "suggestionsList", null, false);   
		}, null, null); 
	}
	function settAllParams(newVal, pasteTargetField){ 
		if (!newVal || !newVal["arrayOfFieldNames"]) return;
		const data = [...stopsArray];
		applyAddressUpdateAndMaybeClearGeo(data[stop.stopOrderNumber], newVal, pasteTargetField);
		for(const fieldName of newVal["arrayOfFieldNames"]){
			const stopParent = document.getElementById('stop'+stop.stopOrderNumber);
			if(stopParent !== null){
				try {
					stopParent.querySelector(`#${fieldName}`).className = stopParent.querySelector(`#${fieldName}`).className.replace('subError', '')
				} catch (error) {
				}
			}
		}
		setStopsArray(data);
	}
	return <div>
		<input type="text" data-mandatory={props.required?true:null} autoComplete={props.autoComplete} name={props.fieldName} 
			placeholder={props.placeHolder} id={props.fieldName} 
			onBlur={props.customCheckTrigger ? (e)=>{props.customCheckTrigger(e, customOnChange, props.fieldName)}  : (e)=>{ 
				if(e.target.value !== ''){
					customOnChange(e.target.value, props.fieldName, e, true);
				}
				for (const child of e.target.nextSibling.childNodes[0].childNodes){
					if(child.matches(':hover')){
						let result = JSON.parse(child.getAttribute('data-raw-val'));
						result['arrayOfFieldNames']=['companyName', 'addressLine1', 'addressLine2', 'city', 'state', 'postalCode', 'direction', "timezone", "latLng"];
						settAllParams(result);
					}
				}
				e.target.nextSibling.style.display = 'none';
			}}
			onPaste={props.customCheckTrigger ? (e)=>{props.customCheckTrigger(e, customOnChange, props.fieldName, true)} : props.customPasteTrigger ? async(e)=>{
				try {
					const result = await props.customPasteTrigger(e); // Wait for async function
					settAllParams(result, props.fieldName);
				} catch (error) {
				}
			} : null}
			value={stop[props.fieldName]}
			onChange={(e)=>{
				if(e.target.value !== ''){
					obtainSuggestionsList(e);
				}else{
					e.target.nextSibling.style.display = 'none';  
				}
				customOnChange(e.target.value, props.fieldName, e, false);
			}}
			onFocus={(e)=>{
				if(e.target.value !== ''){
					obtainSuggestionsList(e);
				}else{
					e.target.nextSibling.style.display = 'none';  
				}
			}}
		/>
		
		{props.suggestionsEnabled ? <OptionsContainer id={props.fieldName+'Options'} style={{display:'none'}} >
			<div>
				{stop['suggestionsList'].map((option, index)=>{
					const displayVal = props.displayValueGenerator(option);
					return <SuggestedOption key={displayVal + index} data-raw-val={JSON.stringify(option)}>{displayVal}
					</SuggestedOption>
				})}
			</div>
		</OptionsContainer> : null}
	</div>;
}

const DateTimeChecking = (e, invalidMessage, customOnChange) =>{
	const formatted = e.target.id === 'apptDate' ? dateFormatter(e.target.value) : timeFormatter(e.target.value);    
	if(formatted.date !== null){
		customOnChange(formatted.date, 'apptDate', e, true)
	}
	if(formatted.time !== null){
		customOnChange(formatted.time, 'apptTime', e, true)
	}
	if ((e.target.id === 'apptDate' && formatted.date === null) || (e.target.id === 'apptTime' && formatted.time === null)){
		invalidMessage();
	}
}
const ZipCodeChecking = (e, invalidMessage, customOnChange, fieldName) =>{
	const zipCheck = isValidZip(e.target.value);
	if(!zipCheck){
		invalidMessage();
	}else{
		customOnChange(zipCheck, fieldName, e, true);
	}
}
const StateValidation = (e, invalidMessage, customOnChange, fieldName) =>{
	const stateVal = isValidState(e.target.value);
	if(!stateVal){
		invalidMessage();
	}else{
		customOnChange(stateVal, fieldName, e, true);
	}
}
const FormStructure = [
	{
		templateName:'template1',
		fields: [{
			fieldName: "companyName",
			placeHolder:"Company Name",
			autoComplete:"new-password",
			required:true,
			suggestionsEnabled: true,
			suggestionsLink: API_ROOT+'locations/locationByCompanyName?pattern=', // link to send request to obtain suggestions
			displayValueGenerator: (val)=>{return `${val['companyName']} (${val['city']}, ${val['state']})`},
			customPasteTrigger:async (e) => {
				return new Promise((resolve) => {
					setTimeout(() => {
						let result = parseAddressWithCompanyName(e.target.value);
						if(!result){
							resolve(null);
						}else{
							resolve(result);
						}
						
					}, 10); 
				});
			}
		},
		{
			fieldType: 'select',
			fieldName: "stopType",
			ingoreInModes : ["newTaskAddressMode"],
			options:[{options:"PU", label:'Pickup'},{options:'DEL', label:'Delivery'},{options:'DEL/PU', label:'Drop/Pick'}]
		}],
	},
	{
		templateName:'template1',
		fields: [
			{
				fieldName: "addressLine1",
				placeHolder:"Address Line 1",
				autoComplete:"new-password",
				customPasteTrigger:async (e) => {
					return new Promise((resolve) => {
						setTimeout(() => {
							let result = parseAddress(e.target.value);
							if(!result){
								resolve(null);
							}else{
								resolve(result);
							}
						}, 10); 
					});
				}
			},
			{
				fieldName: "apptDate",
				placeHolder:"Date",
				customCheckTrigger:checkTrigger,
				autoComplete:"off",
				required:true,
				calendarPickType: 'singleDate',
				onFocus:(stopIndex)=>{
					const todaysDate = new Date();
					todaysDate.setDate(todaysDate.getDate() + stopIndex)
					const currentMoth = `${todaysDate.getMonth()+1}`;
					const possibleDate = `${todaysDate.getDate()}`;
					return `${currentMoth.length === 1 ? '0':''}${currentMoth}/${possibleDate.length === 1 ? '0':''}${possibleDate}`
				}
			},
		]
	},
	{
		templateName:'template1',
		fields: [
			{
				fieldName: "addressLine2",
				placeHolder:"Address Line 2",
				autoComplete:"off",
			},
			{
				fieldName: "apptTime",
				autoComplete:"off",
				placeHolder:"Time",
				customCheckTrigger:checkTrigger
			},
		]
	},
	{
		templateName:'template3',
		fields: [
			{
				fieldName: "city",
				placeHolder:"City",
				autoComplete:"new-password",
				customFormatter:cityNameFormatter,
				required:true,
			},
			{
				fieldName: "state",
				placeHolder:"State",
				autoComplete:"new-password",
				required:true,
				customCheckTrigger:checkTrigger
			},
			{
				fieldName: "postalCode",
				placeHolder:"Zip Code",
				autoComplete:"new-password",
				required:true,
				customCheckTrigger:checkTrigger
			},
			
		]
	},
	{
		templateName:"template1",
		fields:[
			{
				fieldName: "refNumber",
				placeHolder:"Reference/Appointment Number",
				autoComplete:"off",
				ingoreInModes : ["newTaskAddressMode"],
			},
			{
				fieldType: 'select',
				fieldName: "loadingType",
				ingoreInModes : ["newTaskAddressMode"],
				options:[{options:'live', label:'Live'}, {options:'hook&drop', label:'Hook/Drop'}]
			}
		]
	},
	{
		templateName:"template1",
		onlyInEditMode: true,
		fields:[
			{
				ingoreInModes : ["newTaskAddressMode"],
				fieldType: "label",
				labelText: "Stop Status:"
			},
			{
				fieldType: 'select',
				fieldName: "status",
				ingoreInModes : ["newTaskAddressMode"],
				options:[{options:'', label:'None'}, {options:'inProgress', label:'On Site'}, {options:'completed', label:'Completed'}]
			}
		]
	},
	{
		templateName:"template1 hidden",
		// onlyInEditMode: true,
		fields:[
			{
				ingoreInModes : ["newTaskAddressMode"],
				fieldName: "lastStopOfTheLoad",
			},
			{
				ingoreInModes : ["newTaskAddressMode"],
				fieldName: "distanceTo",
			},
			{
				ingoreInModes : ["newTaskAddressMode"],
				fieldName: "possiblyLate",
			},
			{
				ingoreInModes : ["newTaskAddressMode"],
				fieldName: "zonedDateTime",
			},
			{
				ingoreInModes : ["newTaskAddressMode"],
				fieldName: "timezone",
			},
			{
				ingoreInModes : ["newTaskAddressMode"],
				fieldName: "latLng",
			}
		]
	}
]
const FormStructureForNewTask = [
	{
		templateName:"noTemplate",
		fields : [ FormStructure[0].fields[0]]
	},
	{
		templateName:"noTemplate",
		fields : [ FormStructure[1].fields[0]]
	},
	{
		templateName:"noTemplate",
		fields : [ FormStructure[2].fields[0]]
	},
	{
		templateName:"noTemplate",
		fields : [ FormStructure[3].fields[0]]
	},
	{
		templateName:"templateHalfAndHald",
		fields : [ FormStructure[3].fields[1], FormStructure[3].fields[2]]
	},
	{
		partName: "dateTime",
		templateName:"templateHalfAndHald",
		fields : [ FormStructure[1].fields[1], FormStructure[2].fields[1]]
	}
]
function parseAddressWithCompanyName(fullAddress) {    
	const splittedCompanyName = splitCompanyAndRest(fullAddress);	
	if(!splittedCompanyName.companyName || !splittedCompanyName.rest ){
		return null;
	}
	const finalResult = parseAddress(splittedCompanyName.rest);
	finalResult.arrayOfFieldNames.push("companyName")
	finalResult.companyName = splittedCompanyName.companyName;    
	return finalResult;
}
function splitCompanyAndRest(str) {
	if (!str) return { companyName: null, rest: null };

	// Match everything up to the first number that follows whitespace
	const match = str.match(/^(.*?)(?=\s\d)(.*)$/);

	if (match) {
		return {
		companyName: match[1].trim().replace(/,+$/, ""),
		rest: match[2].trim(),
		};
	}

	// No whitespace+number found: treat entire string as company name
	return { companyName: str.trim(), rest: "" };
}
function parseAddress(fullAddress) {
	if (!fullAddress || typeof fullAddress !== "string") return null;

	// --- helper data ---
	const STATE_MAP = {
		"ALABAMA":"AL","ALASKA":"AK","ARIZONA":"AZ","ARKANSAS":"AR","CALIFORNIA":"CA",
		"COLORADO":"CO","CONNECTICUT":"CT","DELAWARE":"DE","FLORIDA":"FL","GEORGIA":"GA",
		"HAWAII":"HI","IDAHO":"ID","ILLINOIS":"IL","INDIANA":"IN","IOWA":"IA",
		"KANSAS":"KS","KENTUCKY":"KY","LOUISIANA":"LA","MAINE":"ME","MARYLAND":"MD",
		"MASSACHUSETTS":"MA","MICHIGAN":"MI","MINNESOTA":"MN","MISSISSIPPI":"MS","MISSOURI":"MO",
		"MONTANA":"MT","NEBRASKA":"NE","NEVADA":"NV","NEW\\s+HAMPSHIRE":"NH","NEW\\s+JERSEY":"NJ",
		"NEW\\s+MEXICO":"NM","NEW\\s+YORK":"NY","NORTH\\s+CAROLINA":"NC","NORTH\\s+DAKOTA":"ND",
		"OHIO":"OH","OKLAHOMA":"OK","OREGON":"OR","PENNSYLVANIA":"PA","RHODE\\s+ISLAND":"RI",
		"SOUTH\\s+CAROLINA":"SC","SOUTH\\s+DAKOTA":"SD","TENNESSEE":"TN","TEXAS":"TX","UTAH":"UT",
		"VERMONT":"VT","VIRGINIA":"VA","WASHINGTON":"WA","WEST\\s+VIRGINIA":"WV","WISCONSIN":"WI",
		"WYOMING":"WY","DISTRICT\\s+OF\\s+COLUMBIA":"DC"
	};

	const STREET_TYPES = [
		"ST","STREET","AVE","AVENUE","RD","ROAD","LN","LANE","BLVD","BOULEVARD",
		"PKWY","PARKWAY","DR","DRIVE","WAY","TERRACE","CT","COURT","PL","PLACE",
		"CIR","CIRCLE","HWY","HIGHWAY","LOOP"
	];

	// --- 1) Remove country tokens safely ---
	fullAddress = fullAddress.replace(/\b(United States of America|United States|USA|US)\b/gi, "");

	// --- 2) Normalize spacing and commas ---
	fullAddress = fullAddress.replace(/\s+/g, " ").trim();
	fullAddress = fullAddress.replace(/\s*,\s*/g, ", ");
	fullAddress = fullAddress.replace(/,+/g, ",");
	fullAddress = fullAddress.replace(/,\s*$/, "");
	fullAddress = fullAddress.replace(/,\s*(\d{5}(?:-\d{4})?)\s*$/, " $1").trim();

	const streetJoinFix = new RegExp(`(\\b(?:${STREET_TYPES.join("|")})\\.?)(?=[A-Z])`, "g");
	fullAddress = fullAddress.replace(streetJoinFix, "$1 ");

	// --- 3) Build state matching regex (full state name or known 2-letter abbrev only).
	// Do not use /[A-Za-z]{2}/ — street suffixes like "Rd"/"St"/"Dr" falsely match as states.
	const stateNamesPattern = Object.keys(STATE_MAP).join("|");
	const stateAbbrevPattern = [...new Set(Object.values(STATE_MAP))].join("|");
	const stateTailRe = new RegExp(
		`(?:,\\s*)?(${stateNamesPattern}|${stateAbbrevPattern})(?:\\s+(\\d{5}(?:-\\d{4})?))?$`,
		"i"
	);

	const endMatch = stateTailRe.exec(fullAddress);
	if (!endMatch) return null;

	let stateToken = endMatch[1].trim();
	const postalCode = endMatch[2] ? endMatch[2].trim() : null;

	// normalize state token -> 2-letter
	let stateAbbrev = null;
	if (stateToken.length === 2) {
		stateAbbrev = stateToken.toUpperCase();
	} else {
		const upper = stateToken.toUpperCase().replace(/\s+/g, " ");
		for (const key of Object.keys(STATE_MAP)) {
		const keyNormalized = key.replace(/\\s\+/g, " ").toUpperCase();
		if (keyNormalized === upper || keyNormalized.replace(/\s+/g,"") === upper.replace(/\s+/g,"")) {
			stateAbbrev = STATE_MAP[key].toUpperCase();
			break;
		}
		}
		if (!stateAbbrev) stateAbbrev = stateToken.slice(0, 2).toUpperCase();
	}

	if (typeof isValidState === "function") {
		stateAbbrev = isValidState(stateAbbrev) ? stateAbbrev : null;
	}
	// Incomplete / false-positive parse (e.g. street-only paste) — leave normal paste alone
	if (!stateAbbrev) return null;

	const head = fullAddress.slice(0, endMatch.index).replace(/,\s*$/, "").trim();

	let addressLine1 = "";
	let city = "";

	if (!head) return null;

	if (head.includes(",")) {
		const idx = head.lastIndexOf(",");
		addressLine1 = head.slice(0, idx).trim();
		city = head.slice(idx + 1).trim();
	} else {
		const stRegex = new RegExp("\\b(" + STREET_TYPES.join("|") + ")\\b\\.?\\s*", "ig");
		let lastMatchEnd = -1;
		while (stRegex.exec(head) !== null) {
		lastMatchEnd = stRegex.lastIndex;
		}
		if (lastMatchEnd > 0) {
		addressLine1 = head.slice(0, lastMatchEnd).trim();
		city = head.slice(lastMatchEnd).trim();
		} else {
		const tokens = head.split(/\s+/);
		if (/^\d/.test(tokens[0])) {
			if (tokens.length === 1) return null;
			city = tokens.slice(-1).join(" ");
			addressLine1 = tokens.slice(0, -1).join(" ");
		} else {
			const mid = Math.max(1, Math.floor(tokens.length / 2));
			addressLine1 = tokens.slice(0, mid).join(" ");
			city = tokens.slice(mid).join(" ");
		}
		}
	}

	addressLine1 = addressLine1.replace(/\s+/g, " ").trim();
	city = city.replace(/\s+/g, " ").trim();
	if (!addressLine1 || !city) return null;

	// --- 6) Extract addressLine2 (suite/apt/unit) from addressLine1 ---
	let addressLine2 = null;
	const unitRe = /\b(?:Suite|Ste|Unit|Apt|Apartment|Fl|Floor|Bldg|Building|Rm|Room|#)\s*[\w-]*$/i;
	const unitMatch = addressLine1.match(unitRe);
	if (unitMatch) {
		addressLine2 = unitMatch[0].trim();
		addressLine1 = addressLine1.slice(0, unitMatch.index).trim();
	}

	const cityFormatted = typeof cityNameFormatter === "function" ? cityNameFormatter(city) : city;
	return {
		addressLine1,
		addressLine2,
		city: cityFormatted,
		state: stateAbbrev || null,
		postalCode,
		arrayOfFieldNames: ["addressLine1", "addressLine2", "city", "state", "postalCode"],
	};
}